/**
 * TerminalTabs - Tab bar for multi-terminal support
 */

import { useState } from 'react';

interface Terminal {
  id: string;
  name: string;
  current_disk: string | null;
  current_cwd: string;
}

interface TerminalTabsProps {
  terminals: Terminal[];
  activeTerminalId: string | null;
  onSelectTerminal: (id: string) => void;
  onCreateTerminal: (name?: string) => Promise<any>;
  onCloseTerminal: (id: string) => void;
  onRenameTerminal: (id: string, name: string) => void;
}

export default function TerminalTabs({
  terminals,
  activeTerminalId,
  onSelectTerminal,
  onCreateTerminal,
  onCloseTerminal,
  onRenameTerminal,
}: TerminalTabsProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');

  const handleDoubleClick = (terminal: Terminal) => {
    setEditingId(terminal.id);
    setEditingName(terminal.name);
  };

  const handleRename = (id: string) => {
    if (editingName.trim()) {
      onRenameTerminal(id, editingName.trim());
    }
    setEditingId(null);
    setEditingName('');
  };

  const handleKeyDown = (e: React.KeyboardEvent, id: string) => {
    if (e.key === 'Enter') {
      handleRename(id);
    } else if (e.key === 'Escape') {
      setEditingId(null);
      setEditingName('');
    }
  };

  return (
    <div className="flex-shrink-0 bg-gray-800 border-b border-gray-700 flex items-center px-2 overflow-x-auto">
      {/* Terminal Tabs */}
      <div className="flex items-center gap-1 py-1">
        {terminals.map(terminal => (
          <div
            key={terminal.id}
            className={`
              group flex items-center gap-2 px-3 py-1.5 rounded-t-md cursor-pointer
              transition-colors duration-150
              ${terminal.id === activeTerminalId
                ? 'bg-gray-900 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }
            `}
            onClick={() => onSelectTerminal(terminal.id)}
            onDoubleClick={() => handleDoubleClick(terminal)}
          >
            {/* Terminal Icon */}
            <svg
              className="w-4 h-4 opacity-60"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>

            {/* Name */}
            {editingId === terminal.id ? (
              <input
                type="text"
                value={editingName}
                onChange={e => setEditingName(e.target.value)}
                onBlur={() => handleRename(terminal.id)}
                onKeyDown={e => handleKeyDown(e, terminal.id)}
                className="bg-gray-800 text-white px-1 w-24 text-sm rounded border border-gray-600 focus:border-blue-500 focus:outline-none"
                autoFocus
                onClick={e => e.stopPropagation()}
              />
            ) : (
              <span className="text-sm truncate max-w-[120px]">
                {terminal.name}
              </span>
            )}

            {/* Disk indicator */}
            {terminal.current_disk && (
              <span className="text-xs text-gray-400 bg-gray-600 px-1.5 rounded">
                {terminal.current_disk}
              </span>
            )}

            {/* Close button */}
            {terminals.length > 1 && (
              <button
                onClick={e => {
                  e.stopPropagation();
                  onCloseTerminal(terminal.id);
                }}
                className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-white transition-opacity"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        ))}
      </div>

      {/* New Terminal Button */}
      <button
        onClick={() => onCreateTerminal()}
        className="ml-1 p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
        title="New Terminal"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      </button>
    </div>
  );
}
